import {
  ctaCreateId,
  ctaMultiCreateId,
  dateId,
  excelDownloadId,
  searchId,
  segmentDisplayId,
} from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import React, { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "query-string";
import {
  Button,
  DataHeader,
  DatePicker,
  Form,
  Grid,
  Input,
  Segmented,
  Select,
  Typography,
} from "@payping/web-components";
import { DashboardRoutes } from "@/router/constant";
import { SDKDateObject, dateFormats, toUtc, utcNow } from "@payping/utils/dateUtils";
import { useAsyncFn } from "react-use";
import { uploadApis } from "@payping/api";
import { PhRDownloadSimple, PhRList, PhRSquaresFour } from "@payping/icons";
import { submitQueryParams } from "@payping/utils/query-string";
import { useAppTranslation } from "@/utils";
import { ListBoolQueryParamsType, ListStringQueryParamsType } from "@/pages/Shop/Product/types";
import { DisplayModeEnum } from "@payping/web-components/components/List/types";
import { DropdownButton } from "@/core/component";

interface Props {
  retry: () => void;
  loading: boolean;
  displayMode: DisplayModeEnum;
  setDisplayMode: React.Dispatch<React.SetStateAction<DisplayModeEnum>>;
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

interface FilterFormType {
  dateValue?: [SDKDateObject | undefined, SDKDateObject | undefined];
  selectValue?: boolean;
  search?: string;
  isArchived?: boolean;
}

const ProductFilter: FC<Props> = (props) => {
  const { displayMode, setDisplayMode, setSelectedRowKeys } = props;
  const { loading, retry } = props;
  const location = useLocation();
  const parsed = qs.parse(location.search);
  const [form] = Form.useForm<FilterFormType>();
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const { searchText, searchDateFrom, searchDateTo }: ListStringQueryParamsType = qs.parse(location.search);
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const backFormat = dateFormats.standard.utcDateTime;
  const { xs, sm, lg, xl } = Grid.useBreakpoint();

  useEffect(() => {
    form.setFieldsValue({
      dateValue: [
        toUtc({ date: searchDateFrom, dateFormat: backFormat, toFormat: backFormat }).object,
        toUtc({ date: searchDateTo, dateFormat: backFormat, toFormat: backFormat }).object,
      ],
      selectValue: Boolean(isArchived),
    });
  }, [backFormat, form, isArchived, searchDateFrom, searchDateTo]);

  const [{ loading: loadingExcel }, generateExcel] = useAsyncFn(async (newWindow: Window | null) => {
    if (!loadingExcel) {
      const path = await uploadApis.productsExportExcelWithWitharchivedWithSearchWithSearchDateFromWithSearchDateToGet(
        isArchived,
        searchText,
        searchDateFrom,
        searchDateTo,
      );
      newWindow?.location?.assign?.(path);
    }
  });

  const mobileFiltersBadgeCount = !lg
    ? {
        searchDateTo,
        searchText,
      }
    : undefined;

  return (
    <Form
      onFinish={(values): void => {
        const date = values.dateValue || [undefined, undefined];
        const [from, to] = [date[0]?.format(backFormat), date[1]?.format(backFormat)];
        const newValues = {
          ...parsed,
          searchDateFrom: from || undefined,
          searchDateTo: to || undefined,
          searchText: values.search,
          isArchived: values.isArchived,
        };
        submitQueryParams({
          navigate,
          newValues,
          parsedSearch: parsed,
          pathname: location.pathname,
          retry,
        });
        setOpenDrawer(false);
        setSelectedRowKeys([]);
      }}
      form={form}
    >
      <DataHeader
        title={
          <Typography.Title className={"!tw-m-0"} level={4}>
            {t("product.title")}
          </Typography.Title>
        }
        primaryAction={
          <DropdownButton
            key={0}
            menu={{
              items: [
                {
                  key: "1",
                  label: (
                    <span data-google-track={ctaMultiCreateId}>{t("product.createBulk.createGroupViaExcel")}</span>
                  ),
                  onClick: (): void => {
                    navigate(
                      { pathname: DashboardRoutes.myShop.product.createBulk.url },
                      { state: { locationSearch: location.search } },
                    );
                  },
                },
              ],
            }}
            type={"primary"}
            onClick={(): void =>
              navigate(
                { pathname: DashboardRoutes.myShop.product.create.url },
                { state: { locationSearch: location.search } },
              )
            }
            data-google-track={ctaCreateId}
          >
            {t("product.add.newProduct")}
          </DropdownButton>
        }
        filters={{
          segmentsButton: sm ? (
            <Segmented
              key={5}
              options={[
                {
                  icon: <PhRSquaresFour width={16} />,
                  label: t("common.displayMode.card"),
                  value: DisplayModeEnum.Card,
                },
                {
                  icon: <PhRList width={16} />,
                  label: t("common.displayMode.list"),
                  value: DisplayModeEnum.List,
                },
              ]}
              value={displayMode}
              onChange={(value) => {
                setDisplayMode(value === DisplayModeEnum.List ? DisplayModeEnum.List : DisplayModeEnum.Card);
              }}
              data-google-track={segmentDisplayId}
            />
          ) : null,
          datepickerInput:
            xs || xl ? (
              <Form.Item key={3} noStyle label={t("createDate")} name={"dateValue"}>
                <DatePicker.RangePicker
                  className={"w-100"}
                  disabledDate={(date) => date.isAfter(utcNow().object)}
                  onChange={() => form.submit()}
                  data-google-track={dateId}
                />
              </Form.Item>
            ) : null,
          searchbarInput: xl ? (
            <Form.Item key={4} noStyle name={"search"}>
              <Input.Search
                data-google-track={searchId}
                allowClear
                onSearch={() => form.submit()}
                placeholder={"نام آیتم / کد آیتم"}
              />
            </Form.Item>
          ) : null,
          filtersButton: (
            <>
              <FiltersButton
                onClick={() => setOpenDrawer(true)}
                params={{
                  isArchived,
                  ...mobileFiltersBadgeCount,
                }}
              />
              <FiltersDrawer
                title={t("filter")}
                onClose={() => setOpenDrawer(false)}
                open={openDrawer}
                onClickApplyFilters={() => {
                  form.submit();
                }}
                onClickResetFilters={() => {
                  form.resetFields();
                  navigate({ pathname: location.pathname });
                  setOpenDrawer(false);
                }}
                loading={loading}
              >
                {!xl && (
                  <>
                    {/* < DatePicker > */}
                    {!xs && (
                      <Form.Item label={t("createDate")} name={"dateValue"} layout={"vertical"}>
                        <DatePicker.RangePicker
                          className={"w-100"}
                          disabledDate={(date) => date.isAfter(utcNow().object)}
                          data-google-track={dateId}
                        />
                      </Form.Item>
                    )}
                    {/* </ DatePicker > */}

                    {/* < Search > */}
                    <Form.Item label={t("report.search")} name={"search"} layout={"vertical"}>
                      <Input data-google-track={searchId} placeholder={t("product.placeholders.search")} />
                    </Form.Item>
                    {/* </ Search > */}
                  </>
                )}

                {/* < isArchived > */}
                <Form.Item label={t("type")} name="isArchived" layout={"vertical"}>
                  <Select
                    allowClear
                    placeholder={t("product.placeholders.archivedUnarchived")}
                    options={[
                      { label: t("archived"), value: true },
                      { label: t("notArchived"), value: false },
                    ]}
                  />
                </Form.Item>
                {/* </ isArchived > */}
              </FiltersDrawer>
            </>
          ),
          downloadButton: (
            <Button
              block
              onClick={() => {
                const newWindow = window.open(undefined, "_blank");
                generateExcel(newWindow);
              }}
              data-google-track={excelDownloadId}
              icon={<PhRDownloadSimple width={14} height={14} />}
            >
              {t("downloadExcel")}
            </Button>
          ),
        }}
      />
    </Form>
  );
};

export default ProductFilter;
